import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/auth-context'
import { getUnreadConversations } from '@/features/projects/projects-api'
import { useProjectConversationsRealtime } from '@/features/projects/use-project-conversations-realtime'

export const unreadConversationsQueryKey = ['unread-conversations'] as const

interface UseUnreadConversationsResult {
  counts: Record<string, number>
  total: number
  unreadForProject: (projectId: string) => number
  leadsTotal: number
  unreadLeadsForProject: (projectId: string) => number
}

export function useUnreadConversations(): UseUnreadConversationsResult {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const summaryQuery = useQuery({
    queryKey: unreadConversationsQueryKey,
    queryFn: getUnreadConversations,
    enabled: isAuthenticated,
    refetchInterval: 30_000,
    retry: false,
  })

  const onEvent = useCallback(
    (event: { event: string; payload: unknown }) => {
      // Непрочитанность меняют только входящие сообщения клиента.
      if (event.event !== 'message.created') {
        return
      }
      const payload = event.payload as { message?: { source_type?: string } } | null
      if (payload?.message?.source_type !== 'visitor') {
        return
      }
      void queryClient.invalidateQueries({ queryKey: unreadConversationsQueryKey })
    },
    [queryClient],
  )

  useProjectConversationsRealtime(onEvent, isAuthenticated)

  const counts = summaryQuery.data?.counts ?? {}
  const total = summaryQuery.data?.total ?? 0

  const unreadForProject = useCallback((projectId: string) => counts[projectId] ?? 0, [counts])

  const leadCounts = summaryQuery.data?.leads?.counts ?? {}
  const leadsTotal = summaryQuery.data?.leads?.total ?? 0

  const unreadLeadsForProject = useCallback(
    (projectId: string) => leadCounts[projectId] ?? 0,
    [leadCounts],
  )

  return { counts, total, unreadForProject, leadsTotal, unreadLeadsForProject }
}
